/**
 * CWIC chat-window resize feature.
 *
 * Renders three drag handles (top edge, left edge, top-left corner) so the
 * user can resize the CWIC chat. Resizes are committed by injecting a
 * stylesheet into CWIC's shadow root with `!important` width / height —
 * needed because CWIC's chat element binds inline width/height through
 * `[style]="positionStyle"` and inline styles otherwise win.
 *
 * NOTE: depends on the CWIC web component exposing a chat element with the
 * class `.copilot-window` inside its (open) shadow root. If the CWIC team
 * renames that class, the resize handles silently disappear.
 */
import { appliedUiScale } from '@helios/utils'

interface CopilotSize {
  width: number
  height: number
}

interface ResizeHandles {
  top: HTMLDivElement
  left: HTMLDivElement
  corner: HTMLDivElement
}

type ResizeDirection = 'top' | 'left' | 'corner'

const HANDLE_THICKNESS = 8
const CORNER_SIZE = 12
const MIN_WIDTH = 320
const MIN_HEIGHT = 240
const VIEWPORT_PADDING = 20

let host: HTMLElement | undefined
let styleEl: HTMLStyleElement | undefined
let userOverride: CopilotSize | undefined
let chatOpen = false
let chatEl: HTMLElement | undefined
let chatStyleObserver: MutationObserver | undefined
let chatResizeObserver: ResizeObserver | undefined
let shadowRootObserver: MutationObserver | undefined
let shadowRootPendingFrame = 0
let windowResizePendingFrame = 0
let handles: ResizeHandles | undefined
let resizeListenerAttached = false

/**
 * Idempotent for the same host. If called with a different host (e.g. an
 * unexpected re-init), tears down state from the previous host and starts
 * fresh against the new one.
 */
export function setupCopilotResizing(newHost: HTMLElement): void {
  if (host === newHost) return
  if (host) resetState()
  host = newHost

  const start = Date.now()
  const tryInject = () => {
    const root = host?.shadowRoot
    if (!root) {
      // CWIC bundle never loaded (network error / blocked); stop polling
      // after 30s so we don't spin a setTimeout for the lifetime of the tab.
      if (Date.now() - start > 30_000) {
        // eslint-disable-next-line no-console
        console.warn(
          'Helios: CWIC bundle did not load within 30s; chat resize handles disabled',
        )
        return
      }
      window.setTimeout(tryInject, 100)
      return
    }
    if (!styleEl || styleEl.getRootNode() !== root) {
      styleEl = document.createElement('style')
      root.appendChild(styleEl)
    }
    watchChatOpenState(root)
  }
  tryInject()

  if (!resizeListenerAttached) {
    resizeListenerAttached = true
    window.addEventListener('resize', scheduleWindowResize)
  }
}

/**
 * Coalesce resize-event bursts (up to ~60fps during a continuous drag) into a
 * single requestAnimationFrame-deferred update, mirroring `scheduleUpdate` —
 * `onWindowResize` does synchronous layout reads/writes that would otherwise
 * force a reflow on every event.
 */
function scheduleWindowResize() {
  if (windowResizePendingFrame) return
  windowResizePendingFrame = window.requestAnimationFrame(() => {
    windowResizePendingFrame = 0
    onWindowResize()
  })
}

function onWindowResize() {
  if (userOverride) {
    // Re-clamp to the new viewport so the chat (and its handles) can't end
    // up off-screen after the window shrinks (e.g. laptop dock).
    userOverride = {
      width: clamp(
        userOverride.width,
        MIN_WIDTH,
        window.innerWidth - VIEWPORT_PADDING,
      ),
      height: clamp(
        userOverride.height,
        MIN_HEIGHT,
        window.innerHeight - VIEWPORT_PADDING,
      ),
    }
  }
  applyOverride()
  positionHandlesFromChat()
}

function resetState() {
  styleEl = undefined
  detachChatTrackers()
  shadowRootObserver?.disconnect()
  shadowRootObserver = undefined
  if (shadowRootPendingFrame) {
    window.cancelAnimationFrame(shadowRootPendingFrame)
    shadowRootPendingFrame = 0
  }
  if (windowResizePendingFrame) {
    window.cancelAnimationFrame(windowResizePendingFrame)
    windowResizePendingFrame = 0
  }
  handles?.top.remove()
  handles?.left.remove()
  handles?.corner.remove()
  handles = undefined
  chatEl = undefined
  chatOpen = false
  userOverride = undefined
}

function applyOverride() {
  if (!styleEl || !userOverride) return
  // computeNewSize works entirely in viewport px (pointer coords, rects,
  // window.inner*); these land as local px on an element inside the zoomed body,
  // so convert on the way out — see appliedUiScale.
  const scale = appliedUiScale()
  styleEl.textContent = `
    .copilot-window {
      width: ${userOverride.width / scale}px !important;
      height: ${userOverride.height / scale}px !important;
    }
  `
}

/**
 * Watch the shadow root for `.copilot-window` appearing / disappearing.
 * Coalesces noisy mutation bursts (e.g. CWIC streaming chat tokens) into a
 * single requestAnimationFrame-deferred update.
 */
function watchChatOpenState(root: ShadowRoot) {
  const update = () => {
    const found = root.querySelector<HTMLElement>('.copilot-window')
    chatOpen = !!found
    setHandlesVisible(chatOpen)
    if (found && found !== chatEl) {
      chatEl = found
      attachChatTrackers(found)
    } else if (!found && chatEl) {
      detachChatTrackers()
      chatEl = undefined
    }
    if (chatEl) {
      ensureHandles()
      positionHandlesFromChat()
    }
  }
  const scheduleUpdate = () => {
    if (shadowRootPendingFrame) return
    shadowRootPendingFrame = window.requestAnimationFrame(() => {
      shadowRootPendingFrame = 0
      update()
    })
  }
  update()
  shadowRootObserver?.disconnect()
  shadowRootObserver = new MutationObserver(scheduleUpdate)
  shadowRootObserver.observe(root, { childList: true, subtree: true })
}

function attachChatTrackers(chat: HTMLElement) {
  detachChatTrackers()
  chatStyleObserver = new MutationObserver(positionHandlesFromChat)
  chatStyleObserver.observe(chat, {
    attributes: true,
    attributeFilter: ['style', 'class'],
  })
  chatResizeObserver = new ResizeObserver(positionHandlesFromChat)
  chatResizeObserver.observe(chat)
}

function detachChatTrackers() {
  chatStyleObserver?.disconnect()
  chatStyleObserver = undefined
  chatResizeObserver?.disconnect()
  chatResizeObserver = undefined
}

function setHandlesVisible(visible: boolean) {
  if (!handles) return
  const display = visible ? 'block' : 'none'
  handles.top.style.display = display
  handles.left.style.display = display
  handles.corner.style.display = display
}

function ensureHandles() {
  if (handles) return
  const top = makeHandle('ns-resize', e => onResize(e, 'top'))
  const left = makeHandle('ew-resize', e => onResize(e, 'left'))
  const corner = makeHandle('nwse-resize', e => onResize(e, 'corner'))
  document.body.append(top, left, corner)
  handles = { top, left, corner }
  setHandlesVisible(chatOpen)
}

function makeHandle(
  cursor: string,
  onPointerDown: (e: PointerEvent) => void,
): HTMLDivElement {
  const el = document.createElement('div')
  el.style.position = 'fixed'
  el.style.cursor = cursor
  el.style.zIndex = '100002'
  el.style.background = 'transparent'
  el.style.touchAction = 'none'
  el.style.userSelect = 'none'
  el.style.display = 'none'
  el.addEventListener(
    'pointerenter',
    () => (el.style.background = 'rgba(0, 0, 0, 0.15)'),
  )
  el.addEventListener(
    'pointerleave',
    () => (el.style.background = 'transparent'),
  )
  el.addEventListener('pointerdown', onPointerDown)
  return el
}

function positionHandlesFromChat() {
  if (!handles || !chatEl) return
  const r = chatEl.getBoundingClientRect()
  const t = HANDLE_THICKNESS
  const c = CORNER_SIZE
  // The rect is viewport px but the handles are appended to the zoomed body, so
  // every value written below is converted into that space — see appliedUiScale.
  // The thicknesses go through it too, so a handle still straddles the edge by
  // the same number of on-screen pixels at any scale.
  const s = appliedUiScale()
  const px = (value: number) => `${value / s}px`

  handles.top.style.left = px(r.left)
  handles.top.style.top = px(r.top - t)
  handles.top.style.width = px(r.width)
  handles.top.style.height = px(t)

  handles.left.style.left = px(r.left - t)
  handles.left.style.top = px(r.top)
  handles.left.style.width = px(t)
  handles.left.style.height = px(r.height)

  handles.corner.style.left = px(r.left - c)
  handles.corner.style.top = px(r.top - c)
  handles.corner.style.width = px(c)
  handles.corner.style.height = px(c)
}

function onResize(ev: PointerEvent, direction: ResizeDirection) {
  if (!handles || !chatEl) return
  const target = ev.currentTarget as HTMLDivElement
  ev.preventDefault()
  target.setPointerCapture(ev.pointerId)

  const startRect = chatEl.getBoundingClientRect()
  const anchorRight = startRect.right
  const anchorBottom = startRect.bottom

  const onMove = (e: PointerEvent) => {
    const baseWidth = userOverride?.width ?? startRect.width
    const baseHeight = userOverride?.height ?? startRect.height
    userOverride = computeNewSize({
      direction,
      anchorRight,
      anchorBottom,
      pointerX: e.clientX,
      pointerY: e.clientY,
      baseWidth,
      baseHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    })
    applyOverride()
  }
  const finish = () => {
    target.releasePointerCapture(ev.pointerId)
    target.removeEventListener('pointermove', onMove)
    target.removeEventListener('pointerup', finish)
    target.removeEventListener('pointercancel', finish)
  }
  target.addEventListener('pointermove', onMove)
  target.addEventListener('pointerup', finish)
  target.addEventListener('pointercancel', finish)
}

/**
 * @internal — exported only for unit tests. Constrain `n` to the inclusive
 * range [lo, hi].
 */
export function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n))
}

/** @internal — exported only for unit tests. */
export interface ComputeNewSizeInput {
  direction: ResizeDirection
  anchorRight: number
  anchorBottom: number
  pointerX: number
  pointerY: number
  baseWidth: number
  baseHeight: number
  viewportWidth: number
  viewportHeight: number
}

/**
 * @internal — exported only for unit tests. Pure size-computation extracted
 * from the drag handler so it can be exercised without DOM events.
 */
export function computeNewSize(input: ComputeNewSizeInput): CopilotSize {
  let width = input.baseWidth
  let height = input.baseHeight
  if (input.direction === 'top' || input.direction === 'corner') {
    height = clamp(
      input.anchorBottom - input.pointerY,
      MIN_HEIGHT,
      input.viewportHeight - VIEWPORT_PADDING,
    )
  }
  if (input.direction === 'left' || input.direction === 'corner') {
    width = clamp(
      input.anchorRight - input.pointerX,
      MIN_WIDTH,
      input.viewportWidth - VIEWPORT_PADDING,
    )
  }
  return { width, height }
}
